import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Printer } from 'lucide-react';
import { useBranding } from '@/lib/branding';
import { engineActive, resolveRules } from '@/lib/formRules';

function SignatureBlock({ field, sig, submissionId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!sig) return;
    api.get(`/forms/submissions/${submissionId}/signatures/${sig.id}/image`, { responseType: 'blob' })
      .then(r => setSrc(URL.createObjectURL(r.data))).catch(() => setSrc(null));
  }, [sig, submissionId]);
  return (
    <div className="mb-3 break-inside-avoid" data-testid={`print-signature-${field.key}`}>
      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{field.label}</div>
      {sig ? (
        <div className="border rounded p-2 mt-1 flex gap-4 items-start">
          {src ? <img src={src} alt={`Signature of ${sig.signer_display_name}`} className="h-20 border-b" /> : <div className="h-20 w-40 border-b text-xs text-gray-400">image unavailable</div>}
          <div className="text-xs leading-5">
            <div><b>Signed by:</b> {sig.signer_display_name} ({sig.signer_capacity})</div>
            <div><b>Method:</b> {sig.signature_method} · <b>Server time (UTC):</b> {sig.signed_at}</div>
            <div><b>Attestation:</b> {sig.attestation_version}</div>
            {sig.authority_verification_status && <div><b>Representative authority:</b> {sig.authority_verification_status.replace(/_/g, ' ').toLowerCase()}</div>}
            {sig.witness_type && <div><b>Witness:</b> authenticated staff</div>}
            {sig.assisted_by_user_id && <div><b>Assisted capture recorded</b> (staff did not sign on behalf)</div>}
          </div>
        </div>
      ) : <div className="border-b border-gray-400 h-12 mt-1" />}
    </div>
  );
}

function PrintField({ field, value }) {
  if (field.type === 'section_header') {
    return <h3 className="text-base font-bold mt-6 mb-1 border-b-2 border-black pb-1 print-section">{field.label}</h3>;
  }
  if (field.type === 'divider') {
    return <hr className="my-3 border-gray-300" />;
  }

  const displayValue = () => {
    if (value === undefined || value === null || value === '') return <span className="text-gray-400 italic">Not provided</span>;
    if (Array.isArray(value)) return value.join(', ') || <span className="text-gray-400 italic">None selected</span>;
    if (field.type === 'yes_no') return <span className={value === 'Yes' ? 'font-semibold' : ''}>{value}</span>;
    if (field.type === 'rating') return `${'★'.repeat(parseInt(value) || 0)}${'☆'.repeat(5 - (parseInt(value) || 0))} (${value}/5)`;
    return String(value);
  };

  return (
    <div className="py-1.5 print-field">
      <div className="flex items-start gap-2">
        <span className="text-sm font-semibold text-gray-700 min-w-[200px] shrink-0">
          {field.label}{field.required && <span className="text-red-600">*</span>}
        </span>
        <span className="text-sm text-gray-900 flex-1">{displayValue()}</span>
      </div>
    </div>
  );
}

export default function FormPrintPage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { organisationName } = useBranding();
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [schema, setSchema] = useState([]);
  const [formData, setFormData] = useState({});
  const [signatures, setSignatures] = useState({});
  const [submissionId, setSubmissionId] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [participantName, setParticipantName] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [aRes, sRes] = await Promise.all([
          api.get(`/forms/assignments/${assignmentId}`),
          api.get(`/forms/assignments/${assignmentId}/submission`),
        ]);
        const a = aRes.data.data;
        setAssignment(a);
        setTemplateName(a.version?.name_snapshot || a.template?.name || 'Form');
        setTemplateDesc(a.template?.description || '');
        setSchema(a.version?.schema_json || []);
        if (sRes.data.data) {
          setFormData(sRes.data.data.data_json || {});
          setSubmissionId(sRes.data.data.id);
          setSubmission(sRes.data.data);
          api.get(`/forms/submissions/${sRes.data.data.id}/signatures`).then(({ data }) => {
            const m = {}; (data.items || []).forEach(sg => { m[sg.field_key] = sg; }); setSignatures(m);
          }).catch(() => {});
        }
        // Load participant name if target_type is PARTICIPANT
        if (a.target_type === 'PARTICIPANT' && a.target_id) {
          try {
            const pRes = await api.get(`/participants/${a.target_id}`);
            const p = pRes.data.data || pRes.data;
            setParticipantName(`${p.first_name || ''} ${p.last_name || ''}`.trim());
          } catch {}
        }
      } catch { toast.error('Failed to load form'); }
      setLoading(false);
    };
    load();
  }, [assignmentId]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-muted-foreground">Loading...</div>;
  if (!assignment) return <div className="text-center py-20 text-muted-foreground">Assignment not found</div>;

  const submissionStatus = assignment.status;
  const hasFilled = Object.keys(formData).length > 0;
  // Engine v1: never print values of fields whose condition was not met. Committed submissions carry the server-recorded
  // hidden_fields (authoritative); a draft on an engine-active version is evaluated with the parity-tested client evaluator.
  // Legacy/no-marker versions: nothing hidden — printed exactly as before.
  const version = assignment.version;
  const rulesActive = engineActive(version);
  const serverHidden = submission?.rules_engine_version != null && Array.isArray(submission.hidden_fields) ? submission.hidden_fields : null;
  const clientHidden = rulesActive ? resolveRules(schema, version.rules_json || [], formData).hidden : new Set();
  const isBlank = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
  // Server record is authoritative for anything that had a value; blank fields whose condition is unmet are listed as N/A rather than "Not provided".
  const hidden = new Set(serverHidden ? [...serverHidden, ...[...clientHidden].filter(k => isBlank(formData[k]))] : [...clientHidden]);
  const notApplicable = schema.filter(f => hidden.has(f.key) && !['section_header', 'divider'].includes(f.type));
  const evaluatedAt = submission?.rules_evaluated_at || null;

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-container { padding: 0 !important; margin: 0 !important; max-width: 100% !important; }
          .print-container * { font-size: 12px !important; }
          .print-container h1 { font-size: 18px !important; }
          .print-container h3 { font-size: 14px !important; }
          .print-field { break-inside: avoid; }
          .print-section { break-before: auto; }
          nav, aside, header, [data-testid="sidebar"], [class*="sidebar"], [class*="Sidebar"] { display: none !important; }
          main { margin: 0 !important; padding: 0 !important; }
        }
      `}</style>

      {/* Screen-only controls */}
      <div className="no-print flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Print Preview</h1>
            <p className="text-sm text-muted-foreground">{templateName}</p>
          </div>
        </div>
        <Button onClick={handlePrint} data-testid="print-btn">
          <Printer className="w-4 h-4 mr-2" />Print Form
        </Button>
      </div>

      {/* Printable content */}
      <div className="print-container max-w-3xl mx-auto bg-white" data-testid="form-print-page">
        {/* Header */}
        <div className="border-b-2 border-black pb-4 mb-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">{templateName}</h1>
              {templateDesc && <p className="text-sm text-gray-600 mt-1 max-w-lg">{templateDesc}</p>}
            </div>
            <div className="text-right text-sm text-gray-600">
              <p className="font-semibold">{organisationName}</p>
              <p>NDIS Provider</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
            {participantName && (
              <div><span className="text-gray-500">Participant:</span><br/><span className="font-semibold">{participantName}</span></div>
            )}
            <div><span className="text-gray-500">Status:</span><br/><span className="font-semibold">{submissionStatus}</span></div>
            {assignment.due_date && (
              <div><span className="text-gray-500">Due Date:</span><br/><span className="font-semibold">{assignment.due_date}</span></div>
            )}
            <div><span className="text-gray-500">Assigned:</span><br/><span className="font-semibold">{assignment.assigned_at?.slice(0, 10) || 'N/A'}</span></div>
            {assignment.template?.form_code && (
              <div><span className="text-gray-500">Form Code:</span><br/><span className="font-semibold">{assignment.template.form_code}</span></div>
            )}
            {rulesActive && version?.version_number != null && (
              <div data-testid="print-template-version"><span className="text-gray-500">Template Version:</span><br/><span className="font-semibold">v{version.version_number}</span></div>
            )}
            {rulesActive && evaluatedAt && (
              <div data-testid="print-rules-evaluated-at"><span className="text-gray-500">Conditions evaluated (UTC):</span><br/><span className="font-semibold">{evaluatedAt}</span></div>
            )}
          </div>
        </div>

        {/* Form Fields */}
        {hasFilled ? (
          <div>
            {schema.filter(field => !hidden.has(field.key)).map((field, i) => (
              field.type === 'signature_drawn'
                ? <SignatureBlock key={field.key || `f-${i}`} field={field} sig={signatures[field.key]} submissionId={submissionId} />
                : <PrintField key={field.key || `f-${i}`} field={field} value={formData[field.key]} />
            ))}
            {notApplicable.length > 0 && (
              <div className="mt-4 pt-2 border-t border-gray-300 text-xs text-gray-600 print-field" data-testid="print-not-applicable">
                <span className="font-semibold">Not applicable because the condition was not met:</span> {notApplicable.map(f => f.label || f.key).join('; ')}
              </div>
            )}
          </div>
        ) : (
          /* Blank form - show fields with empty lines for handwriting */
          <div>
            {schema.map((field, i) => {
              if (field.type === 'section_header') {
                return <h3 key={i} className="text-base font-bold mt-6 mb-1 border-b-2 border-black pb-1">{field.label}</h3>;
              }
              if (field.type === 'divider') {
                return <hr key={i} className="my-3 border-gray-300" />;
              }
              const isMultiLine = ['long_text', 'address'].includes(field.type);
              const hasOptions = ['dropdown', 'multi_select', 'radio', 'checkbox'].includes(field.type);
              return (
                <div key={field.key || `f-${i}`} className="py-2 print-field">
                  <div className="text-sm font-semibold text-gray-700 mb-1">
                    {field.label}{field.required && <span className="text-red-600">*</span>}
                    {field.type === 'yes_no' && <span className="font-normal text-gray-500 ml-2">[ ] Yes  [ ] No</span>}
                  </div>
                  {hasOptions && field.options?.length > 0 && (
                    <div className="text-xs text-gray-500 mb-1">
                      {field.options.map((o, oi) => (
                        <span key={oi} className="mr-3">[ ] {o}</span>
                      ))}
                    </div>
                  )}
                  {!hasOptions && field.type !== 'yes_no' && (
                    <div className={`border-b border-gray-400 ${isMultiLine ? 'min-h-[60px]' : 'min-h-[24px]'}`}>&nbsp;</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-4 border-t border-gray-300 text-xs text-gray-500">
          <div className="flex justify-between">
            <span>Printed from {organisationName} — {new Date().toLocaleDateString()}</span>
            <span>{assignment.template?.form_code || ''}</span>
          </div>
        </div>
      </div>
    </>
  );
}
